<template>
  <USButton
    v-if="user.isPlayer || user.isAdmin"
    icon="plus"
    :icon-size="14"
    type="inline"
    label="Create Event"
    @click="isOpen = true"
  />
  <AddNewModal
    v-model:isOpen="isOpen"
    input-id="add-event-name"
    name="add-event-name"
    :heading="'Create New Event at ' + clubName"
    description="Enter event name to get started:"
    placeholder="Event Name"
    button-label="Create"
    :ask-for-non-golf-event="true"
    :is-non-golf-club="isNonGolfClub"
    @add="handleCreate"
  />
  <USToast
    v-show="serverError"
    class="create-event-btn__server-error"
    content="Something went wrong"
    semantic="error"
    icon="exclamation-square"
    @click="serverError = false"
  />
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { useRouter } from 'vue-router'

import { useClient } from '@base/api/useClient'
import { useUserStore } from '@base/store/userStore'
import { CREATE_EVENT_STEPPED_MUTATION } from '@base/event-add/api/mutations/createEventStepped'
import { createEventStepped } from '__generated__/createEventStepped'
import AddNewModal from '@player/_components/AddNewModal/AddNewModal.vue'
import USButton from '@base/components/USButton/USButton.vue'
import USToast from '@base/components/USToast/USToast.vue'

export default defineComponent({
  components: { AddNewModal, USButton, USToast },
  props: {
    clubId: {
      type: String,
      required: true
    },
    clubName: {
      type: String,
      required: true
    },
    isNonGolfClub: {
      type: Boolean,
      required: true
    },
    eventRouteName: {
      type: String,
      required: true
    },
    eventRouteParam: {
      type: String,
      required: true
    }
  },
  setup(props) {
    // Initial
    const client = useClient()
    const router = useRouter()
    const user = useUserStore()

    // Input

    // Output
    const serverError = ref(false)
    const loading = ref(false)
    const isOpen = ref(false)

    // Mutation
    const handleCreate = async (eventName: string, isNonGolf: boolean) => {
      if (loading.value) return

      loading.value = true

      serverError.value = false

      try {
        const { data } = await client.mutate<createEventStepped>({
          mutation: CREATE_EVENT_STEPPED_MUTATION,
          variables: {
            eventName,
            clubId: props.clubId,
            isNonGolf
          }
        })

        if (
          !data ||
          data.createEvent.__typename !== 'CreateEventOutput' ||
          !data.createEvent.event
        ) {
          serverError.value = true
          return
        }
        const newId = data.createEvent.event.id

        isOpen.value = false

        return await router.push({
          name: props.eventRouteName,
          params: { [props.eventRouteParam]: newId }
        })
      } catch (e) {
        console.error(e)
        serverError.value = true
      } finally {
        loading.value = false
        isOpen.value = false
      }
    }

    // Return
    return { serverError, loading, handleCreate, isOpen, user }
  }
})
</script>

<style lang="scss" scoped>
.create-event-btn {
  &__server-error {
    bottom: 5%;
    cursor: pointer;
    position: fixed;
    right: var(--grid-horizontal-space);
    z-index: 50;
  }
}

:deep(.us-icon) {
  --us-icon-diameter: 14px;
}
</style>
